import React from "react";
import { motion } from "framer-motion";
import { Leaf, Palette, Package, ShieldCheck, Sparkles } from "lucide-react";

const PROMISES = [
  { icon: Sparkles, title: "Rich Aroma", desc: "Volatile oils preserved at 4–6% — pungent, sweet, lasting." },
  { icon: Leaf, title: "Natural Green Colour", desc: "Slow-cured to retain emerald hue. No artificial dyes." },
  { icon: Package, title: "Fresh Stock", desc: "Inventory rotated within 90 days. Never warehouse-aged." },
  { icon: ShieldCheck, title: "Hygienic Packaging", desc: "Food-grade vacuum bags. FSSAI-compliant facility." },
  { icon: Palette, title: "Consistent Quality", desc: "Every batch QC-tested before dispatch. Lot-traceable." },
];

const PROD_IMG =
  "https://images.unsplash.com/photo-1701166627787-12d9fdd437cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxncmVlbiUyMGNhcmRhbW9tJTIwbWFjcm98ZW58MHx8fHwxNzgxMjYxMjMwfDA&ixlib=rb-4.1.0&q=85";

export const QualityPromise = () => {
  return (
    <section
      id="quality"
      data-testid="quality-section"
      className="section-pad bg-white relative"
    >
      <div className="container-luxury">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={PROD_IMG}
                alt="Premium green cardamom"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E4D3D]/30 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block">
              <div className="bg-[#0E4D3D] text-white p-6 max-w-[230px]">
                <div className="font-serif text-3xl text-[#C9A227]">100%</div>
                <div className="label-overline text-white/80 mt-1">
                  Lab-tested batches
                </div>
                <p className="text-white/70 text-xs mt-3 font-light">
                  Every shipment carries a quality certificate of analysis.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="heading-eyebrow">
                <span className="line" />
                <span className="text">The Quality Promise</span>
              </div>
              <h2 className="font-serif text-3xl md:text-5xl text-[#0E4D3D] leading-tight">
                What every kilo carries.
              </h2>
              <p className="mt-5 text-[#4A4A4A] text-base md:text-lg font-light leading-relaxed max-w-xl">
                Five non-negotiables that travel with every wholesale shipment —
                the reason 300+ businesses across Bangalore source from us, year
                after year.
              </p>
            </motion.div>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {PROMISES.map((p, i) => {
                const Icon = p.icon;
                return (
                  <motion.div
                    key={p.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ delay: i * 0.08, duration: 0.6 }}
                    className="flex gap-4 p-5 border border-[#0E4D3D]/10 hover:border-[#C9A227] hover:bg-[#FAF8F2] transition-all duration-500 group"
                    data-testid={`quality-item-${i}`}
                  >
                    <div className="shrink-0 w-10 h-10 flex items-center justify-center text-[#0E4D3D] group-hover:text-[#C9A227] transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg text-[#0E4D3D]">
                        {p.title}
                      </h3>
                      <p className="text-[#4A4A4A] text-sm mt-1 font-light leading-relaxed">
                        {p.desc}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
